import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { Loader } from 'components/atoms/Loader';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useQuery } from 'hooks/useQuery';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { StyledPageTitle } from 'theme/styles';
import { useLicenceContext } from 'models/licenceContext';
import { getLegalEntityAddress } from 'requests/legalEntities';
import {
  EAssetType,
  ILegalEntityAddress,
  ILegalEntityAddressDetail,
} from 'models/legalEntity';
import { LocationMap } from './locationMap';
import { Stack } from '@mui/material';

const ViewLegalEntityAddressMapPage = () => {
  const { id = '', addressId = '' } = useParams<{ id: string; addressId: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [legalEntityAddress, setLegalEntityAddress] = useState<ILegalEntityAddress>();
  const [legality, setLegality] = useState<string>();
  const [assetTypeSelected, setAssetTypeSelected] = useState<EAssetType | null>(null);
  const [filteredLegalEntityAddressDetails, setFilteredLegalEntityAddressDetails] =
    useState<ILegalEntityAddressDetail>();
  const { t } = useTranslations();

  const query = useQuery();
  const navigate = useNavigate();
  const type: string | null = query.get('type')?.trim() || null;

  const { legalEntityAddressDetails, getLegalEntityAddressDetailsData } = useLegalEntities();
  const { isOnlineLicence } = useLicenceContext();

  useEffect(() => {
    (async function getLegalEntityAddressDataInit() {
      if (id && addressId) {
        setIsLoading(true);
        getLegalEntityAddressDetailsData(id, addressId);

        const response = await getLegalEntityAddress(id, addressId);
        if (response) {
          setLegalEntityAddress(response);
        }
        setIsLoading(false);
      }
    })();
  }, [id, addressId]);

  document.addEventListener('setAssetTypeEvent', (data: any) => {
    const typeSelected = data.detail.data;
    setAssetTypeSelected(typeSelected);
  });

  useEffect(() => {
    (async function getLegalEntityAddressDataInit() {
      if (legalEntityAddressDetails) {
        let filteredData = {
          firearms:
            !assetTypeSelected || assetTypeSelected === EAssetType.firearms
              ? legalEntityAddressDetails.firearms
              : [],
          accessories:
            !assetTypeSelected || assetTypeSelected === EAssetType.accessories
              ? legalEntityAddressDetails.accessories
              : [],
          ammunition:
            !assetTypeSelected || assetTypeSelected === EAssetType.ammunition
              ? legalEntityAddressDetails.ammunition
              : [],
          components:
            !assetTypeSelected || assetTypeSelected === EAssetType.components
              ? legalEntityAddressDetails.components
              : [],
        };

        if (legality) {
          filteredData = {
            firearms: filteredData.firearms.filter(
              (item) => item.legalityUniqueId === legality,
            ),
            accessories: filteredData.accessories.filter(
              (item) => item.legalityUniqueId === legality,
            ),
            ammunition: filteredData.ammunition.filter(
              (item) => item.legalityUniqueId === legality,
            ),
            components: filteredData.components.filter(
              (item) => item.legalityUniqueId === legality,
            ),
          };
        }

        setFilteredLegalEntityAddressDetails(filteredData);
      }
    })();
  }, [legality, legalEntityAddressDetails, assetTypeSelected]);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
          {
            label:
              type === ELegalEntityTypes.individual
                ? t('legalEntities.individual.accordion.label')
                : t('legalEntities.organization.accordion.label'),
            route: `${ERouteLinks.LegalEntities}?expand=${type}`,
          },
          {
            label: t('legalEntityDetails.legalEntityDetails.text'),
            route: `${ERouteLinks.ViewLegalEntity.replace(
              ':id',
              id,
            )}?type=${type}&expand=address`,
          },
          t('legalEntityDetails.address.header'),
          t('legalEntityDetails.address.showOnMap.header'),
        ]}
      />
      {isLoading ? (
        <Loader isLoading={isLoading} />
      ) : (
        <Grid container>
          <Stack sx={{ gridColumn: { xs: 'span 12' }, gap: 2 }}>
            <StyledPageTitle variant="h4">
              {t('legalEntityDetails.address.addressShowOnMap.text')}
            </StyledPageTitle>
            <Button
              label={t('general.back.button')}
              id="back"
              variant={EButtonVariants.outlined}
              icon={EIconTypes.left}
              onClick={() =>
                navigate(
                  `${ERouteLinks.ViewLegalEntity.replace(
                    ':id',
                    id,
                  )}?type=${type}&expand=address`,
                )
              }
              size={EButtonSizes.medium}
              sx={{ width: '100px' }}
            />
          </Stack>
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <LocationMap
              firearms={filteredLegalEntityAddressDetails?.firearms ?? []}
              accessories={filteredLegalEntityAddressDetails?.accessories ?? []}
              ammunition={filteredLegalEntityAddressDetails?.ammunition ?? []}
              components={filteredLegalEntityAddressDetails?.components ?? []}
              assetTypeSelected={assetTypeSelected}
              latitude={Number(legalEntityAddress?.latitude)}
              longitude={Number(legalEntityAddress?.longitude)}
              showLegalityBar={true}
              updateLegality={setLegality}
              isOnline={isOnlineLicence()}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export { ViewLegalEntityAddressMapPage };
